import React from 'react';
import {
  Bell,
  Send,
  CheckCircle
} from 'lucide-react';

const AlertsTab = ({
  alerts,
  alertForm,
  setAlertForm,
  onSubmitAlert,
  onAcknowledgeAlert,
  isLoading,
  error,
  success,
  user,
  getSeverityColor
}) => {
  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4 sm:p-6">
        <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white mb-3 sm:mb-4 flex items-center">
          <Bell className="w-4 h-4 sm:w-5 sm:h-5 mr-2 text-orange-500" />
          Send New Emergency Alert
        </h3>
        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
            <p className="text-red-600">{error}</p>
          </div>
        )}
        {success && (
          <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-md">
            <p className="text-green-600">{success}</p>
          </div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Alert Title *
            </label>
            <input
              type="text"
              value={alertForm.title}
              onChange={(e) => setAlertForm(prev => ({ ...prev, title: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              placeholder="e.g., Fire Alarm, Evacuation Notice"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Alert Type
            </label>
            <select
              value={alertForm.alert_type}
              onChange={(e) => setAlertForm(prev => ({ ...prev, alert_type: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
            >
              <option value="general">General</option>
              <option value="fire">Fire</option>
              <option value="evacuation">Evacuation</option>
              <option value="lockdown">Lockdown</option>
              <option value="chemical_hazard">Chemical Hazard</option>
              <option value="severe_weather">Severe Weather</option>
              <option value="medical_emergency">Medical Emergency</option>
              <option value="security_threat">Security Threat</option>
              <option value="equipment_failure">Equipment Failure</option>
            </select>
          </div>
        </div>
        <div className="mt-4">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Message *
          </label>
          <textarea
            value={alertForm.message}
            onChange={(e) => setAlertForm(prev => ({ ...prev, message: e.target.value }))}
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
            placeholder="Detailed alert message..."
          />
        </div>
        <div className="mt-4 flex items-center justify-between">
          <label className="flex items-center text-sm font-medium text-gray-700 dark:text-gray-300">
            <input
              type="checkbox"
              checked={alertForm.acknowledgment_required}
              onChange={(e) => setAlertForm(prev => ({ ...prev, acknowledgment_required: e.target.checked }))}
              className="mr-2"
            />
            Acknowledgment Required
          </label>
          <button
            onClick={onSubmitAlert}
            disabled={isLoading}
            className="px-6 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 disabled:opacity-50 flex items-center"
          >
            <Send className="w-4 h-4 mr-2" />
            {isLoading ? 'Sending...' : 'Send Alert'}
          </button>
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md">
        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            Active Alerts ({alerts.length})
          </h3>
        </div>
        <div className="divide-y divide-gray-200 dark:divide-gray-700">
          {alerts.length === 0 ? (
            <div className="p-8 text-center text-gray-500 dark:text-gray-400">
              No active emergency alerts.
            </div>
          ) : (
            alerts.map((alert) => (
              <div key={alert.id} className="p-4 hover:bg-gray-50 dark:hover:bg-gray-700">
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2 mb-2">
                      <h4 className="font-medium text-gray-900 dark:text-white">
                        {alert.title}
                      </h4>
                      <span className={`px-2 py-1 text-xs rounded-full ${getSeverityColor(alert.priority)}`}>
                        {alert.priority}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 dark:text-gray-300 mb-2">
                      {alert.message}
                    </p>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      Sent: {new Date(alert.timestamp).toLocaleString()}
                    </div>
                  </div>
                  <div className="flex-shrink-0 w-full sm:w-auto">
                    {alert.acknowledgment_required && !alert.acknowledged_by.includes(user?.id) && (
                      <button
                        onClick={() => onAcknowledgeAlert(alert.id)}
                        className="w-full sm:w-auto px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm"
                      >
                        Acknowledge
                      </button>
                    )}
                    {alert.acknowledgment_required && alert.acknowledged_by.includes(user?.id) && (
                      <span className="w-full sm:w-auto flex items-center justify-center px-3 py-2 bg-green-100 text-green-800 rounded-md text-sm">
                        <CheckCircle size={16} className="mr-2" />
                        Acknowledged
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default AlertsTab;
